/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import FeatherIcon from "feather-icons-react/build/FeatherIcon";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Modal, Button } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import SidebarAdmin from "../SidebarAdmin";
import BusinessHeader from "../BusinessHeader";
import config from "../../config";

const AddDepartment = () => {
  const [formData, setFormData] = useState({
    departmentName: "",
    parentDepartment: "",
  });

  const [departments, setDepartments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentDepartmentId, setCurrentDepartmentId] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editDepartment, setEditDepartment] = useState(null);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [hasPermission, setHasPermission] = useState(null); // null indicates loading

  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm({
    mode: "onChange",
  });

  const modalForm = useForm({
    mode: "onChange",
  });

  const businessId = localStorage.getItem("BusinessID");

  // Fetch user permissions
  const fetchPermissions = async () => {
    try {
      const businessID = localStorage.getItem('BusinessID');
      const userID = localStorage.getItem('userUUID');


      if (!businessID || !userID) {
        console.error("Business ID or User ID not found in localStorage");
        setHasPermission(false);
        return;
      }

      const response = await fetch(`${config.API_URL}get_admins_permissions.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ business_id: businessID, user_id: userID }),
      });

      const data = await response.json();
      if (data.status === "success" && data.permissions) {
        setHasPermission(data.permissions.add_department === 1);
      } else {
        console.error("Failed to fetch permissions:", data.message);
        setHasPermission(false);
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      setHasPermission(false);
    }
  };

  const fetchDepartments = () => {
    if (!businessId) {
      toast.error("Business ID not found. Please log in again.", {
        position: "top-center",
      });
      return;
    }

    fetch(`${config.API_URL}get_departments.php?business_id=${businessId}`)
      .then((response) => response.json())
      .then((data) => {
        const departmentList = Array.isArray(data) ? data : (data.status === "success" ? data.data : []);
        setDepartments(departmentList);
        console.log("Fetched departments:", departmentList); // Debug log
      })
      .catch((error) => {
        console.error("Error fetching departments:", error);
        toast.error("Failed to load departments", {
          position: "top-center",
        });
      });
  };

  useEffect(() => {
    fetchPermissions();
    fetchDepartments();
  }, []);

  const handleFormSubmit = async (data) => {
    if (!data.departmentName.trim()) {
      toast.error("Department name is required", {
        position: "top-center",
      });
      return;
    }

    const payload = {
      department_name: data.departmentName,
      parent_department_id: formData.parentDepartment || null,
      business_id: businessId,
    };

    const url = isEditMode
      ? `${config.API_URL}update_department.php`
      : `${config.API_URL}add_department.php`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(isEditMode ? { ...payload, id: currentDepartmentId } : payload),
      });

      const result = await response.json();
      if (result.status === "success") {
        toast.success(result.message, {
          position: "top-center",
        });
        resetForm();
        fetchDepartments();
      } else {
        toast.error(result.message || "Operation failed", {
          position: "top-center",
        });
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred", {
        position: "top-center",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      departmentName: "",
      parentDepartment: "",
    });
    setValue("departmentName", "");
    setIsEditMode(false);
    setCurrentDepartmentId(null);
    reset();
  };

  const handleEdit = (department) => {
    setEditDepartment(department);
    modalForm.setValue("departmentName", department.department_name);
    modalForm.setValue("parentDepartment", department.parent_department_id || "");
    setCurrentDepartmentId(department.id);
    setShowModal(true);
    console.log("Opening edit modal for department:", department); // Debug log
  };

  const handleModalSubmit = async (data) => {
    if (!data.departmentName.trim()) {
      toast.error("Department name is required", {
        position: "top-center",
      });
      return;
    }

    const payload = {
      id: currentDepartmentId,
      department_name: data.departmentName,
      parent_department_id: data.parentDepartment || null,
      business_id: businessId,
    };

    try {
      const response = await fetch(`${config.API_URL}update_department.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (result.status === "success") {
        toast.success(result.message, {
          position: "top-center",
        });
        setShowModal(false);
        modalForm.reset();
        fetchDepartments();
      } else {
        toast.error(result.message || "Failed to update department", {
          position: "top-center",
        });
      }
    } catch (error) {
      console.error("Error updating department:", error);
      toast.error("An error occurred", {
        position: "top-center",
      });
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    modalForm.reset();
    setEditDepartment(null);
    setCurrentDepartmentId(null);
    console.log("Closing edit modal"); // Debug log
  };

  const handleCheckboxChange = (id) => {
    setSelectedDepartments((prev) =>
      prev.includes(id)
        ? prev.filter((deptId) => deptId !== id)
        : [...prev, id]
    );
    console.log("Selected departments:", selectedDepartments); // Debug log
  };

  const handleBulkSelect = (e) => {
    if (e.target.checked) {
      const allIds = [];
      const collectIds = (depts) => {
        depts.forEach((dept) => {
          allIds.push(dept.id);
          if (dept.children) collectIds(dept.children);
        });
      };
      collectIds(filteredDepartments);
      setSelectedDepartments(allIds);
    } else {
      setSelectedDepartments([]);
    }
    console.log("Bulk select:", e.target.checked ? "All selected" : "All unselected"); // Debug log
  };

  const handleBulkDelete = async () => {
    if (selectedDepartments.length === 0) return;

    if (!window.confirm(`Are you sure you want to delete ${selectedDepartments.length} department(s)?`)) {
      return;
    }

    try {
      const deletePromises = selectedDepartments.map((id) =>
        fetch(`${config.API_URL}delete_department.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id }),
        }).then((response) => response.json())
      );

      const results = await Promise.all(deletePromises);
      const failed = results.filter((result) => result.status !== "success");

      if (failed.length === 0) {
        toast.success("Selected departments deleted successfully", {
          position: "top-center",
        });
        setSelectedDepartments([]);
        fetchDepartments();
      } else {
        toast.error("Failed to delete some departments", {
          position: "top-center",
        });
      }
    } catch (error) {
      console.error("Error deleting departments:", error);
      toast.error("An error occurred while deleting", {
        position: "top-center",
      });
    }
    console.log("Bulk delete triggered for IDs:", selectedDepartments); // Debug log
  };

  const toggleExpand = (id) => {
    setExpandedNodes((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const renderDepartmentOptions = (depts, level = 0) => {
    console.log("Rendering department options, level:", level, "departments:", depts); // Debug log
    return depts.map((dept) => (
      <React.Fragment key={dept.id}>
        <option value={dept.id}>
          {level > 0 ? "-".repeat(level) : ""} {dept.department_name}
        </option>
        {dept.children && renderDepartmentOptions(dept.children, level + 1)}
      </React.Fragment>
    ));
  };

  const filterDepartments = (depts, term) => {
    return depts
      .map((dept) => {
        const children = dept.children ? filterDepartments(dept.children, term) : [];
        if (
          dept.department_name.toLowerCase().includes(term.toLowerCase()) ||
          children.length > 0
        ) {
          return { ...dept, children };
        }
        return null;
      })
      .filter(Boolean);
  };

  const filteredDepartments = searchTerm ? filterDepartments(departments, searchTerm) : departments;

  const renderTree = (depts, level = 0) => {
    console.log("Rendering tree with departments:", depts, "Level:", level); // Debug log
    return depts.map((dept) => (
      <React.Fragment key={dept.id}>
        <tr>
          <td>
            <input
              type="checkbox"
              checked={selectedDepartments.includes(dept.id)}
              onChange={() => handleCheckboxChange(dept.id)}
            />
          </td>
          <td>
            <div className="d-flex align-items-center">
              {dept.children && dept.children.length > 0 ? (
                <button
                  className="btn btn-sm btn-icon me-2"
                  onClick={() => toggleExpand(dept.id)}
                  style={{ padding: "0", background: "transparent", border: "none" }}
                >
                  <FeatherIcon
                    icon={expandedNodes[dept.id] ? "chevron-down" : "chevron-right"}
                    size={16}
                  />
                </button>
              ) : (
                <span style={{ width: "24px", display: "inline-block" }}></span>
              )}
              <span
                style={{
                  marginLeft: `${level * 20}px`,
                  fontWeight: dept.department_name.toLowerCase().includes(searchTerm.toLowerCase())
                    ? "bold"
                    : "normal",
                }}
              >
                {level > 0 ? "-".repeat(level) : ""} {dept.department_name}
              </span>
            </div>
          </td>
          <td>
            {departments.find(d => d.id === dept.parent_department_id)?.department_name || "-"}
          </td>
          <td>
            <span
              className="text-primary cursor-pointer"
              onClick={() => handleEdit(dept)}
            >
              Edit
            </span>
          </td>
        </tr>
        {dept.children &&
          dept.children.length > 0 &&
          expandedNodes[dept.id] && renderTree(dept.children, level + 1)}
      </React.Fragment>
    ));
  };

  // Render loading state while checking permissions
  if (hasPermission === null) {
    return (
      <div>
        <BusinessHeader />
        <SidebarAdmin />
        <div className="page-wrapper">
          <div className="content">
            <p>Loading permissions...</p>
          </div>
        </div>
      </div>
    );
  }

  // Render access denied message if user lacks permission
  if (!hasPermission) {
    return (
      <div>
        <BusinessHeader />
        <SidebarAdmin />
        <div className="page-wrapper">
          <div className="content">
            <div className="alert alert-danger">
              You do not have permission to manage departments.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <BusinessHeader />
      <SidebarAdmin id="menu-item6" id1="menu-items6" activeClassName="add-department" />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="#">Department</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <FeatherIcon icon="chevron-right" />
                  </li>
                  <li className="breadcrumb-item active">
                    {isEditMode ? "Edit Department" : "Add Department"}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSubmit(handleFormSubmit)}>
                    <div className="form-group local-forms">
                      <label>
                        Department Name <span className="login-danger">*</span>
                      </label>
                      <input
                        type="text"
                        {...register("departmentName", {
                          required: "Department name is required",
                          pattern: {
                            value: /^[A-Za-z\s]+$/,
                            message: "Only letters and spaces are allowed",
                          },
                        })}
                        className="form-control"
                        value={formData.departmentName}
                        onChange={(e) =>
                          setFormData({ ...formData, departmentName: e.target.value })
                        }
                      />
                      {errors.departmentName && (
                        <span className="text-danger">{errors.departmentName.message}</span>
                      )}
                    </div>

                    <div className="form-group local-forms mt-3">
                      <label>Parent Department</label>
                      <select
                        className="form-control"
                        value={formData.parentDepartment}
                        onChange={(e) =>
                          setFormData({ ...formData, parentDepartment: e.target.value })
                        }
                      >
                        <option value="">Select Parent Department</option>
                        {renderDepartmentOptions(departments)}
                      </select>
                    </div>

                    <div className="doctor-submit text-end mt-4">
                      <button
                        type="submit"
                        className="btn btn-primary submit-form me-2"
                      >
                        {isEditMode ? "Update Department" : "Add Department"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary cancel-form"
                        onClick={resetForm}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4>Existing Departments</h4>
                    {selectedDepartments.length > 0 && (
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={handleBulkDelete}
                      >
                        Delete Selected ({selectedDepartments.length})
                      </button>
                    )}
                  </div>
                  <div className="mb-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search departments..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>
                            <input
                              type="checkbox"
                              onChange={handleBulkSelect}
                              checked={
                                filteredDepartments.length > 0 &&
                                selectedDepartments.length === 
                                filteredDepartments.reduce((acc, dept) => {
                                  acc.push(dept.id);
                                  if (dept.children) acc.push(...dept.children.map(c => c.id));
                                  return acc;
                                }, []).length
                              }
                            />
                          </th>
                          <th>Name</th>
                          <th>Parent</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDepartments.length > 0 ? (
                          renderTree(filteredDepartments)
                        ) : (
                          <tr>
                            <td colSpan="4" className="text-center">
                              No departments found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={handleModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Department</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={modalForm.handleSubmit(handleModalSubmit)}>
            <div className="form-group local-forms">
              <label>
                Department Name <span className="login-danger">*</span>
              </label>
              <input
                type="text"
                {...modalForm.register("departmentName", {
                  required: "Department name is required",
                  pattern: {
                    value: /^[A-Za-z\s]+$/,
                    message: "Only letters and spaces are allowed",
                  },
                })}
                className="form-control"
              />
              {modalForm.formState.errors.departmentName && (
                <span className="text-danger">
                  {modalForm.formState.errors.departmentName.message}
                </span>
              )}
            </div>

            <div className="form-group local-forms mt-3">
              <label>Parent Department</label>
              <select
                {...modalForm.register("parentDepartment")}
                className="form-control"
              >
                <option value="">Select Parent Department</option>
                {renderDepartmentOptions(departments)}
              </select>
            </div>

            <div className="text-end mt-4">
              <Button
                type="submit"
                variant="primary"
                className="me-2"
              >
                Update Department
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={handleModalClose}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <ToastContainer />
    </div>
  );
};

export default AddDepartment;